"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { recordingMimeType, type Transcribe } from "./speech";

/* ------------------------------------------------------------------ */
/* Microphone level                                                    */
/* ------------------------------------------------------------------ */

function rms(analyser: AnalyserNode, data: Uint8Array<ArrayBuffer>) {
	analyser.getByteTimeDomainData(data);
	let sum = 0;
	for (const value of data) {
		const centered = (value - 128) / 128;
		sum += centered * centered;
	}
	// Speech sits around 0.02-0.2 RMS; stretch it so the visual moves.
	return Math.min(1, Math.sqrt(sum / data.length) * 4.5);
}

/* ------------------------------------------------------------------ */
/* Recording                                                           */
/* ------------------------------------------------------------------ */

interface Recording {
	stream: MediaStream;
	startedAt: number;
	/** Stops the mic and resolves with what was recorded. */
	finish: () => Promise<Blob | null>;
	/** Stops the mic and throws the audio away. */
	cancel: () => void;
}

/**
 * Opens the mic and records until finished. Throws a DOMException
 * (NotAllowedError) when the shopper refuses the mic, or "unsupported".
 */
async function record(): Promise<Recording> {
	const mimeType = recordingMimeType();
	if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
		throw new Error("unsupported");
	}
	const stream = await navigator.mediaDevices.getUserMedia({
		audio: {
			echoCancellation: true,
			noiseSuppression: true,
			autoGainControl: true,
		},
	});
	const recorder = new MediaRecorder(stream, { mimeType });
	const chunks: Blob[] = [];
	recorder.ondataavailable = (event) => {
		if (event.data.size) {
			chunks.push(event.data);
		}
	};
	recorder.start();
	const release = () => {
		for (const track of stream.getTracks()) {
			track.stop();
		}
	};
	return {
		stream,
		startedAt: Date.now(),
		finish: () =>
			new Promise((resolve) => {
				if (recorder.state === "inactive") {
					release();
					resolve(null);
					return;
				}
				recorder.onstop = () => {
					release();
					resolve(
						chunks.length
							? new Blob(chunks, { type: mimeType })
							: null,
					);
				};
				recorder.stop();
			}),
		cancel: () => {
			recorder.onstop = null;
			if (recorder.state !== "inactive") {
				recorder.stop();
			}
			release();
		},
	};
}

/* ------------------------------------------------------------------ */
/* Voice notes                                                         */
/* ------------------------------------------------------------------ */

export interface VoiceNote {
	/** Local playback only; the server gets the transcript. */
	audioUrl: string | null;
	durationMs: number;
	/** 0..1 levels, one per ~100 ms, for the waveform. */
	bars: number[];
	transcript: string;
	/** What the server heard it as: "ar", "en" or "fr". */
	lang: string;
}

const BAR_INTERVAL = 100;
/** Shorter than this is not a message: a press, not someone talking. */
export const MIN_NOTE_MS = 700;
/** The server refuses clips over 60 s; say so instead of uploading one. */
const MAX_NOTE_MS = 60_000;

/** Levels kept for the live waveform while recording. */
const LIVE_BARS = 28;

/**
 * Records while the mic is held, sampling levels for the waveform (the live
 * one while recording, and the note's own afterwards). On release the clip is
 * transcribed on the server; the audio stays here.
 */
export function useVoiceNote(transcribe: Transcribe) {
	const [live, setLive] = useState<{
		startedAt: number;
		/** The latest levels, oldest first, for the live waveform. */
		levels: number[];
	} | null>(null);
	const [transcribing, setTranscribing] = useState(false);
	const parts = useRef<{
		recording: Recording;
		bars: number[];
		sampler: number;
		context: AudioContext;
	} | null>(null);

	// Each note's playback URL pins its audio in memory until revoked: freed with the chat.
	const [clipUrls] = useState(() => new Map<Blob, string>());
	useEffect(
		() => () => {
			for (const url of clipUrls.values()) {
				URL.revokeObjectURL(url);
			}
			clipUrls.clear();
		},
		[clipUrls],
	);

	const release = useCallback(() => {
		const p = parts.current;
		parts.current = null;
		if (p) {
			clearInterval(p.sampler);
			void p.context.close();
		}
		setLive(null);
		return p;
	}, []);

	const start = useCallback(async () => {
		const active = await record();
		const context = new AudioContext();
		// Created after an await, so iOS starts it suspended.
		void context.resume();
		const analyser = context.createAnalyser();
		analyser.fftSize = 512;
		context.createMediaStreamSource(active.stream).connect(analyser);
		const data = new Uint8Array(analyser.fftSize);
		const bars: number[] = [];
		parts.current = {
			recording: active,
			bars,
			sampler: window.setInterval(() => {
				bars.push(rms(analyser, data));
				setLive((current) =>
					current
						? { ...current, levels: bars.slice(-LIVE_BARS) }
						: current,
				);
			}, BAR_INTERVAL),
			context,
		};
		setLive({ startedAt: active.startedAt, levels: [] });
	}, []);

	/** Resolves with the note, or `{ error }` when there is nothing to send. */
	const finish = useCallback(async (): Promise<
		| { note: VoiceNote }
		| {
				error:
					| "too_short"
					| "no_audio"
					| "too_long"
					| "no_speech"
					| "rate_limited"
					| "paused"
					| "failed";
		  }
		| null
	> => {
		const p = release();
		if (!p) {
			return null;
		}
		const durationMs = Date.now() - p.recording.startedAt;
		const clip = await p.recording.finish();
		// The microphone gave nothing at all (its track ended, another app holds it): not the
		// shopper's doing, and not to be told apart from a short press without its own error.
		if (!clip) {
			return { error: "no_audio" };
		}
		if (durationMs < MIN_NOTE_MS) {
			return { error: "too_short" };
		}
		if (durationMs > MAX_NOTE_MS) {
			return { error: "too_long" };
		}
		setTranscribing(true);
		const result = await transcribe(clip);
		setTranscribing(false);
		if (!result.ok) {
			return { error: result.error };
		}
		// Lives as long as the composer (the page's chat): the bubble plays it back.
		clipUrls.set(clip, URL.createObjectURL(clip));
		return {
			note: {
				audioUrl: clipUrls.get(clip) ?? null,
				durationMs,
				bars: p.bars,
				transcript: result.text,
				lang: result.language,
			},
		};
	}, [release, transcribe, clipUrls]);

	const cancel = useCallback(() => {
		release()?.recording.cancel();
	}, [release]);

	useEffect(() => () => release()?.recording.cancel(), [release]);

	return {
		recording: live !== null,
		startedAt: live?.startedAt ?? 0,
		/** Latest levels, oldest first; the last one is the level now. */
		levels: live?.levels ?? [],
		transcribing,
		start,
		finish,
		cancel,
	};
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Squeezes (or stretches) levels to exactly `count` bars. */
export function resampleBars(bars: readonly number[], count: number) {
	if (!bars.length) {
		return Array.from({ length: count }, () => 0.15);
	}
	return Array.from({ length: count }, (_, i) => {
		const from = Math.floor((i / count) * bars.length);
		const to = Math.max(
			from + 1,
			Math.floor(((i + 1) / count) * bars.length),
		);
		const slice = bars.slice(from, to);
		return Math.max(0.12, Math.max(...slice));
	});
}

export function formatDuration(ms: number) {
	const seconds = Math.max(0, Math.round(ms / 1000));
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Seconds since `since`, ticking while `active`. */
export function useElapsed(since: number, active: boolean) {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!active) {
			return;
		}
		setNow(Date.now());
		// The clocks show whole seconds.
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, [active]);
	return active ? now - since : 0;
}
